import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from '@ui/atoms';
import {
    oppListingFilterKeys,
    OPPORTUNITY_BOND_TYPES,
    SORT_OPTIONS,
} from './utils/constants';
import { useStoreSelector } from '@ui/hooks/useStoreSelector';
import useOpportunitiesStore from './store/useOpportunitiesStore';
import OpportunityHeader from './components/OpportunityHeader';
import OpportunityListingResults from './components/OpportunityListingResults';
import useWindowDimensions from '@hooks/useWindowDimensions';
import BottomDrawer from '@ui/atoms/design-system/bottom-drawer';
import SortMenu from './components/FilterAndSort/SortMenu';
import FilterModal from './components/FilterAndSort/FilterModal';
import { configurationEntityTypes } from '@helpers/enums/configuration-enums';
import dynamic from 'next/dynamic';
import useKycStore from '@store/useKycStore';
import { getOpenConfigurations } from '@services/configuration.service';
import { isValidResponseEntity } from '@helpers/utils';
import { parseAndCheckFestiveSeason } from '@helpers/miscelleanous';
import useOpportunitiesInvestment from '@hooks/functional/useOpportunitiesInvestment';
import useAuthStore from '@store/useAuthStore';
import { useRouter } from 'next/router';
import MuhuratDaysBanner from './components/MuhuratDaysBanner';
import RbiRegulatedSdiNoteModal from './components/RbiRegulatedSdiNoteModal';
import FilteredOpportunityHeader from '@components/OpportunityListing/components/FilteredOpportunityHeader';

const KycAlertInfo = dynamic(() =>
    import('@molecules/KycBanners/KycAlertInfo')
);
const PendingOrderNudge = dynamic(() => import('@molecules/PendingOrderNudge'));
const NseAddBeneficiaryBanner = dynamic(() =>
    import('@molecules/KycBanners/NseAddBeneficiaryBanner')
);

const OpportunityListing = ({ activities, showActiveOpportunities = true }) => {
    const router = useRouter();
    const { width } = useWindowDimensions();

    const {
        showSortModal,
        showFilterModal,
        closeSortModal,
        closeFilterModal,
        isAuthorized,
        count,
        isFetching,
    } = useStoreSelector(useOpportunitiesStore, (state) => ({
        showSortModal: state.showSortModal,
        showFilterModal: state.showFilterModal,
        closeSortModal: state.closeSortModal,
        closeFilterModal: state.closeFilterModal,
        isAuthorized: state.isAuthorized,
        count: state.data.count,
        isFetching: state.isFetching,
    }));
    const {
        showKycBanner,
        showNseBeneficiaryBanner,
        isNsePending,
        isDematVerified,
    } = useStoreSelector(useKycStore, (state) => ({
        showKycBanner: state.showKycBanner,
        showNseBeneficiaryBanner: state.showNseBeneficiaryBanner,
        isNsePending: state.isNsePending,
        isDematVerified: state.isDematVerified,
    }));

    const { investor } = useAuthStore();

    const {
        promisedAmoOrderCount,
        pendingAmoOrderCount,
        pendingOrderCount,
        setAmoNudge,
        isAmoTradingAllowed,
        isPostMarketHours,
        isPreMarketHours,
        isTradingAllowed,
    } = useOpportunitiesInvestment(null, false, isAuthorized);

    const pendingOrders = useMemo(
        () =>
            (isTradingAllowed ? promisedAmoOrderCount : 0) +
            pendingAmoOrderCount +
            pendingOrderCount,
        [
            isAmoTradingAllowed,
            promisedAmoOrderCount,
            pendingAmoOrderCount,
            pendingOrderCount,
            isPostMarketHours,
            isPreMarketHours,
        ]
    );
    const showNudge = useMemo(
        () => showKycBanner || isNsePending || !isDematVerified,
        [showKycBanner, isNsePending, isDematVerified]
    );
    const [showFestiveDays, setShowFestiveDays] = useState(null);
    const [showRbiRegulatedNote, setShowRbiRegulatedNote] = useState(false);
    const getFestiveDays = async () => {
        try {
            const festiveDaysResponse = await getOpenConfigurations(
                configurationEntityTypes.Festive_Season
            );
            if (
                isValidResponseEntity(festiveDaysResponse) &&
                festiveDaysResponse?.entity?.length
            ) {
                const isFestiveDay = parseAndCheckFestiveSeason(
                    festiveDaysResponse?.entity?.[0].value
                );
                setShowFestiveDays(isFestiveDay);
            } else {
                setShowFestiveDays(null);
            }
        } catch (error) {
            setShowFestiveDays(null);
        }
    };

    const handleRbiRegulatedNote = () => {
        // Check if the opportunity is SDI
        const isSDI = router?.query?.type === OPPORTUNITY_BOND_TYPES.SDIS;
        if (isSDI) {
            // If the note has been shown before, don't show it again
            if (sessionStorage.getItem('RbiRegulatedNoteShown') === 'true') {
                setShowRbiRegulatedNote(false);
            } else if (showActiveOpportunities) {
                // If not shown, set flag and display the note
                setShowRbiRegulatedNote(true);
                sessionStorage.setItem('RbiRegulatedNoteShown', 'true');
            }
        }
    };

    useEffect(() => {
        if (showActiveOpportunities) getFestiveDays();
    }, []);

    useEffect(() => {
        if (!showNudge && !showNseBeneficiaryBanner) setAmoNudge(investor?.id);
    }, [isAmoTradingAllowed, showNudge, showNseBeneficiaryBanner]);

    useEffect(() => {
        if (isAuthorized) {
            handleRbiRegulatedNote();
        }
    }, [router.query, isAuthorized]);

    const hasFilters = useMemo(() => {
        const { query } = router;
        return oppListingFilterKeys.some((param) => param in query);
    });

    const renderBanner = () => {
        let bannerComponent;
        if (
            showFestiveDays?.type === configurationEntityTypes.MuhuratDays &&
            showFestiveDays?.isActive
        ) {
            bannerComponent = <MuhuratDaysBanner />;
        } else if (showNudge) {
            bannerComponent = (
                <div className="flex justify-center w-full px-4 md:px-0 md:w-auto">
                    <KycAlertInfo />
                </div>
            );
        } 
        /* Not removing the showNseBeneficiaryBanner related code 
        incase we want to show it again in future */
        /* else if (showNseBeneficiaryBanner) {
            bannerComponent = (
                <div className="px-4 md:px-0">
                    <NseAddBeneficiaryBanner />
                </div>
            );
        } */
        else if (pendingOrders > 0) {
            bannerComponent = (
                <div className="px-4 md:px-0">
                    <PendingOrderNudge orderCount={pendingOrders} />
                </div>
            );
        } else {
            bannerComponent = null;
        }
        return bannerComponent ? (
            <div className="mt-4 md:mt-6">{bannerComponent}</div>
        ) : null;
    };

    return (
        <section className="relative flex flex-col min-h-screen pt-16 bg-opportunities-gradient">
            {hasFilters ? (
                <FilteredOpportunityHeader
                    opportunitiesCount={count}
                    isAuthorized={isAuthorized}
                    isFetching={isFetching}
                />
            ) : (
                <OpportunityHeader
                    showActiveOpportunities={showActiveOpportunities}
                    renderBanner={renderBanner}
                />
            )}
            <OpportunityListingResults
                showActiveOpportunities={showActiveOpportunities}
                activities={activities}
                showFestiveDays={showFestiveDays}
                hasFilters={hasFilters}
            />
            {/* Modals */}
            {showFilterModal &&
                (width > 768 ? (
                    <Modal
                        show={showFilterModal}
                        onClose={closeFilterModal}
                        width="w-full max-w-[472px] m-0 md:m-0"
                        label={'Filter Bonds'}
                        labelClassName="text-primary-500 h5-semibold"
                        paddingClass="p-0"
                        roundedClass="overflow-hidden rounded-xl md:rounded-2xl"
                    >
                        <FilterModal onClose={closeFilterModal} />
                    </Modal>
                ) : (
                    <BottomDrawer
                        visible={showFilterModal}
                        onClose={closeFilterModal}
                        title="Filter Bonds"
                        titleClassName="text-left h5-semibold self-start mr-auto text-primary-500"
                        wrapperClass="max-h-[80%] overflow-auto"
                        textWrapperClass="z-10"
                    >
                        <FilterModal onClose={closeFilterModal} />
                    </BottomDrawer>
                ))}
            {showSortModal &&
                (width < 768 ? (
                    <BottomDrawer
                        visible={showSortModal}
                        onClose={closeSortModal}
                        title="Sort"
                        titleClassName="text-left h5-semibold self-start mr-auto text-primary-500"
                        textWrapperClass="z-10"
                        wrapperClass="max-h-[80%] overflow-auto"
                    >
                        <SortMenu
                            onClose={closeSortModal}
                            sortOptions={SORT_OPTIONS}
                            defaultSort={SORT_OPTIONS[0].value}
                        />
                    </BottomDrawer>
                ) : null)}
            {showRbiRegulatedNote && isAuthorized && (
                <RbiRegulatedSdiNoteModal
                    show={true}
                    onClose={() => setShowRbiRegulatedNote(false)}
                />
            )}
        </section>
    );
};

export default OpportunityListing;
